import { FC, ReactElement, useEffect } from 'react';
import { Control, FieldError, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ComboboxOption } from '@/components/controls/CustomDropdown';
import CustomInput from '@/components/controls/CustomInput';
import CustomSelect from '@/components/controls/CustomSelect';

export const defaultInputStyle =
  'w-full px-4 py-2.5 text-base placeholder:text-honoursPlaceholder outline-none whitespace-nowrap rounded-lg border border-solid border-primary-300 text-zinc-800 focus:border-honoursBlue';

export const defaultLabelStyle =
  'flex flex-col mb-3 text-md whitespace-nowrap text-primary-100 font-medium';

const defaultDivStyle = '';

type FieldType = 'input' | 'select';

export interface EntityFormFieldSpecification {
  name: string;
  label: string;
  fieldType?: FieldType;
  type?: string;
  placeholder?: string;
  selectOptions?: ComboboxOption[];
  disabledInEdit?: boolean;
  styles?: {
    input?: string;
    label?: string;
    div?: string;
  };
  icon?: ReactElement;
  onIconClick?: () => void;
  validation?: {
    required?: boolean;
    maxLength?: number;
    minLength?: number;
  };
  onChange?: (value: any) => void;
  searchable?: boolean;
  watchFields?: string[];
  dynamicDisabled?: (watchedValues: any[]) => boolean;
  dynamicHidden?: (watchedValues: any[]) => boolean;
}

interface CustomEntityFormFieldProps {
  field: EntityFormFieldSpecification;
  control: Control<any>;
  error?: FieldError;
  disabled: boolean;
  clearErrors?: (name?: any) => void;
  setValue?: (name: any, value: any, options?: any) => void;
}

const CustomEntityFormField: FC<CustomEntityFormFieldProps> = ({
  field,
  control,
  error,
  disabled,
  clearErrors,
  setValue,
}) => {
  const watchedValues = useWatch({
    control,
    name: field.watchFields || [],
  });

  const isDynamicallyDisabled = field.dynamicDisabled
    ? field.dynamicDisabled(watchedValues as any[])
    : false;
  const finalDisabled = disabled || isDynamicallyDisabled;

  useEffect(() => {
    if (isDynamicallyDisabled) {
      if (error && clearErrors) {
        clearErrors(field.name);
      }
      if (setValue) {
        setValue(field.name, 0, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [isDynamicallyDisabled, error, clearErrors, setValue, field.name]);

  const isHidden = field.dynamicHidden
    ? field.dynamicHidden(watchedValues as any[])
    : false;

  const inputStyle = twMerge(defaultInputStyle, field.styles?.input);
  const labelStyle = twMerge(defaultLabelStyle, field.styles?.label);
  const divStyle = twMerge(defaultDivStyle, field.styles?.div);

  if (isHidden) {
    return null;
  }

  if (field.fieldType && field.fieldType === 'select') {
    return (
      <CustomSelect
        name={field.name}
        label={field.label}
        control={control}
        selectOptions={field.selectOptions || []}
        placeholder={field.placeholder ?? ''}
        divStyle={divStyle}
        labelStyle={labelStyle}
        selectStyle={inputStyle}
        error={error}
        disabled={finalDisabled}
        mandatory={field.validation?.required}
        onChange={field.onChange}
        searchable={field.searchable}
      />
    );
  }

  return (
    <CustomInput
      name={field.name}
      type={field.type}
      label={field.label}
      placeholder={field.placeholder}
      hasIcon={!!field.icon}
      inputStyle={inputStyle}
      labelStyle={labelStyle}
      divStyle={divStyle}
      icon={field.icon}
      onIconClick={field.onIconClick}
      control={control}
      error={error}
      disabled={finalDisabled}
      maxLength={field.validation?.maxLength}
      mandatory={field.validation?.required}
      onChange={field.onChange}
    />
  );
};

export default CustomEntityFormField;
